import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Users, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { searchClubs, searchUsers } from '../services/api';
import defaultAvatar from '../assets/default-avatar.jpg';

function SearchBar({ onSearch }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState({ clubs: [], users: [] });
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
    setQuery('');
    if (onSearch) onSearch();
  };

  // Fermer la recherche en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (query.trim().length >= 2) {
        handleSearch();
      } else {
        setResults({ clubs: [], users: [] });
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  const handleSearch = async () => {
    if (query.trim().length < 2) return;

    setLoading(true);
    try {
      const [clubsData, usersData] = await Promise.all([
        searchClubs(query),
        searchUsers(query)
      ]);

      setResults({
        clubs: clubsData,
        users: usersData
      });
      setIsOpen(true);
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClubClick = (clubId) => {
    handleNavigate(`/club/${clubId}`);
  };

  const handleUserClick = (userId) => {
    handleNavigate(`/user/${userId}`);
  };

  const clearSearch = () => {
    setQuery('');
    setResults({ clubs: [], users: [] });
    setIsOpen(false);
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-3 text-gray-400" size={20} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim().length >= 2 && setIsOpen(true)}
          placeholder="Rechercher des clubs ou des profils..."
          className="w-full pl-10 pr-10 py-2 bg-slate-800 border border-slate-700 text-white placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-300"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Résultats de recherche */}
      {isOpen && (
        <div className="absolute top-full mt-2 w-full bg-slate-900 border border-slate-800 rounded-lg shadow-xl max-h-96 overflow-y-auto z-50">
          {loading ? (
            <div className="p-4 text-center text-gray-400">Recherche...</div>
          ) : (
            <>
              {/* Résultats Clubs */}
              {results.clubs.length > 0 && (
                <div className="p-2">
                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                    Clubs ({results.clubs.length})
                  </p>
                  {results.clubs.map((club) => (
                    <button
                      key={club._id}
                      onClick={() => handleClubClick(club._id)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-800 border border-transparent hover:border-slate-700 rounded-lg transition"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-indigo-900/30 border border-indigo-500 rounded-full flex items-center justify-center">
                          <Users size={20} className="text-indigo-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white truncate">{club.nom}</p>
                          <div className="flex items-center text-sm text-gray-400 space-x-2">
                            <MapPin size={14} />
                            <span>{club.ville}</span>
                            <span>•</span>
                            <span>{club.niveau}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Résultats Utilisateurs */}
              {results.users.length > 0 && (
                <div className="p-2 border-t border-slate-800">
                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                    Profils ({results.users.length})
                  </p>
                  {results.users.map((user) => (
                    <button
                      key={user._id}
                      onClick={() => handleUserClick(user._id)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-800 border border-transparent hover:border-slate-700 rounded-lg transition"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <img
                            src={user.photoProfil || defaultAvatar}
                            alt={`${user.prenom} ${user.nom}`}
                            className="w-10 h-10 rounded-full object-cover border-2 border-slate-700"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white truncate">
                            {user.prenom} {user.nom}
                          </p>
                          {user.ville && (
                            <div className="flex items-center text-sm text-gray-400">
                              <MapPin size={14} className="mr-1" />
                              <span>{user.ville}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Aucun résultat */}
              {results.clubs.length === 0 && results.users.length === 0 && query.trim().length >= 2 && (
                <div className="p-4 text-center text-gray-400">
                  Aucun résultat pour "{query}"
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchBar;